import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { TypographyGuard } from "../components/typography-guard";
import { initClickTracking, initOfferViews } from "../lib/analytics";
import { initGa } from "../lib/ga";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <title>Nie znaleziono strony | Sylwia Sitkowska</title>
      <meta name="robots" content="noindex, follow" />
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Nie znaleziono strony</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Ta strona nie istnieje lub została przeniesiona. Sprawdź{" "}
          <Link to="/sitemap" className="underline underline-offset-4">
            mapę strony
          </Link>{" "}
          albo wróć na stronę główną.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Strona główna
          </Link>
          <Link
            to="/kontakt"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Kontakt
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Nie udało się wczytać strony
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Coś poszło nie tak po naszej stronie. Spróbuj odświeżyć albo wróć na stronę główną.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Sylwia Sitkowska" },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { name: "google-site-verification", content: "9xUvo4-4MTdhRos5AamhaaOh6XRT_hezT2J06yOA9UI" },
      { name: "google-site-verification", content: "R-FVzeq8MZgmRVpFpZXCOtV89jCaZqbMVt31fGW5d4w" },

      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pl_PL" },
      { property: "og:site_name", content: "Sylwia Sitkowska" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [
      {
        // Google Tag Manager (kontener GTM-W463663S), wg instrukcji Google
        // do wklejenia jak najwyżej w <head>.
        type: "text/javascript",
        // GTM ładuje się wyłącznie na produkcyjnych hostach (z www i bez);
        // podgląd i localhost nie uruchamiają kontenera.
        children: `if(location.hostname==='sylwiasitkowska.pl'||location.hostname==='www.sylwiasitkowska.pl'){(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-W463663S');}`,
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "preconnect", href: "https://daunntkwcoqcrupnnfuf.supabase.co" },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/fira-sans-latin-ext-normal.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/dm-serif-display-latin-ext-normal.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/dm-serif-display-latin-ext-italic.woff2",
        crossOrigin: "anonymous",
      },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});


function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pl">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => initClickTracking(), []);
  useEffect(() => initOfferViews(), []);

  // Google Analytics 4: podpięcie przy starcie (page_view przy nawigacji obsługuje src/lib/ga.ts)
  useEffect(() => {
    void initGa();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <TypographyGuard />
    </QueryClientProvider>
  );
}
